import { pieArcLabelClasses, PieChart, useDrawingArea } from '@mui/x-charts';
import { Box, Card, CardContent, Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Strings } from '@app/constants';
import { StyledText } from './styles';
import { CustomAvatarGroup, CustomAvatar } from '../custom-avatar';
import {
  ITaskOverview,
  selectGetTaskOverviewByIdState,
  getTasksOverviewById,
} from '@app/modules/dashboard';
import { useRequestState } from '@app/hooks';
import { DataNotFound } from '../page-404';

interface Props {
  taskOverview: ITaskOverview;
  selectedProject?: number | null;
}
const PieChartCard = ({ taskOverview, selectedProject }: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [selectedMember, setSelectedMember] = useState('');

  function PieCenterLabel({ children }: { children: React.ReactNode }) {
    const { width, height, left, top } = useDrawingArea();
    return (
      <StyledText x={left + width / 2} y={top + height / 3}>
        {children}
      </StyledText>
    );
  }

  const colors = [
    theme.palette.quaternary.darkBlue,
    theme.palette.quaternary.lightGreen,
    theme.palette.quaternary.darkGreen,
    theme.palette.quaternary.blue,
    theme.palette.error.main,
  ];

  const { data } = useRequestState({
    stateSelector: selectGetTaskOverviewByIdState,
    errorShown: true,
  });

  const taskStatus: Record<string, number> = {};

  if (selectedMember) {
    data?.lists?.forEach((list: ITaskOverview) => {
      if (!taskStatus[list.listName]) {
        taskStatus[list.listName] = 0;
      }
      taskStatus[list.listName] += list.listTasks;
    });
  } else {
    taskOverview?.lists?.forEach((list: ITaskOverview) => {
      if (!taskStatus[list.listName]) {
        taskStatus[list.listName] = 0;
      }
      taskStatus[list.listName] += list.listTasks;
    });
  }
  const totalTasks = taskOverview?.totalTasks ?? 0;

  const pieData =
    totalTasks > 0
      ? Object.keys(taskStatus).map((listName, index) => {
          const percentage = (taskStatus[listName] / totalTasks) * 100;
          return {
            label: listName,
            value: taskStatus[listName],
            percentage: percentage.toFixed(1),
            color: colors[index % colors.length],
          };
        })
      : [
          {
            label: 'No Data',
            value: 1,
            color: theme.palette.grey[400],
          },
        ];

  useEffect(() => {
    if (selectedProject) {
      dispatch(getTasksOverviewById({ projectId: selectedProject }));
      setSelectedMember('');
    }
  }, [selectedProject, dispatch]);

  return (
    <Card
      sx={{
        borderRadius: '5px',
        ml: { xs: 1, md: 0 },
        mr: { xs: 1, md: 0 },
        height: { md: 330 },
        boxShadow: { xs: 0, md: 1 },
      }}
    >
      <CardContent>
        <Stack flexDirection={'row'} justifyContent={'space-between'}>
          <Stack fontSize={17} fontWeight={700} mt={-2}>
            {Strings.dashboard.taskOverview}
          </Stack>
          <Stack mt={-2}>
            <CustomAvatarGroup max={9}>
              {taskOverview?.users?.length > 0 &&
                taskOverview?.users?.map((member: ITaskOverview, index: number) => (
                  <CustomAvatar
                    key={index}
                    name={`${member.firstName} ${member.lastName}`}
                    color="info"
                  />
                ))}
            </CustomAvatarGroup>
          </Stack>
        </Stack>
        {totalTasks > 0 ? (
          <>
            <Box
              sx={{
                width: '100%',
                height: 180,
                marginTop: { xs: -2, md: 0 },
                padding: 0,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <PieChart
                series={[
                  {
                    data: pieData.map((item, index) => ({
                      label: item.label,
                      value: item.value,
                      color: item.color,
                    })),
                    cx: '55%',
                    cy: '30%',
                    innerRadius: 55,
                    outerRadius: 90,
                  },
                ]}
                slotProps={{
                  legend: { hidden: true },
                }}
                sx={{
                  [`& .${pieArcLabelClasses.root}`]: {
                    fill: 'white',
                    fontSize: 10,
                  },
                  margin: 0,
                  padding: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  paddingLeft: { md: '75px', xs: 0 },
                  marginLeft: { xs: 7, md: 0 },
                  mt: { xs: 2, md: 0 },
                }}
                height={300}
                width={300}
              >
                <PieCenterLabel>
                  <tspan
                    fontWeight={'bolder'}
                    dx={'7px'}
                    dy={'-15px'}
                    style={{ display: 'flex', justifySelf: 'center' }}
                  >
                    {totalTasks ?? 0}
                  </tspan>
                  <tspan
                    dy="20px"
                    dx={totalTasks && totalTasks > 10 ? '-60' : '-50'}
                    fill={theme.palette.grey[700]}
                    fontSize={16}
                  >
                    {Strings.projects.totalTasks}
                  </tspan>
                </PieCenterLabel>
              </PieChart>
            </Box>
            {totalTasks > 0 && (
              <Stack
                direction={'row'}
                spacing={2}
                gap={1.5}
                ml={-1}
                mt={1.5}
                justifyContent={'center'}
                sx={{ flexWrap: 'wrap' }}
              >
                {pieData.map((item, index) => (
                  <Stack direction={'row'} gap={1} mt={2} key={index}>
                    <Typography
                      width={{ md: 15, xs: 10, sm: 13 }}
                      height={{ md: 15, xs: 10, sm: 13 }}
                      borderRadius={'50%'}
                      bgcolor={item.color}
                      mt={0.3}
                    />
                    <Typography fontSize={{ md: 13, xs: 8, sm: 13 }}>{item.label}</Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </>
        ) : (
          <DataNotFound height={300} />
        )}
      </CardContent>
    </Card>
  );
};

export { PieChartCard };
